/**
 * Kaksos Portal API Configuration
 *
 * Auth goes through peelers-api (identity authority)
 * All other API calls go through kaksos-api
 */

/// <reference types="vite/client" />

// API Configuration
export const AUTH_API_URL = (import.meta.env?.VITE_AUTH_API_URL as string) || 'https://peelers.getboby.ai';

// Token storage keys
const TOKEN_KEY = 'boby_kaksos_token';
const USER_KEY = 'boby_kaksos_user';

/**
 * Get the stored auth token
 */
export function getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
}

/**
 * Store auth token
 */
export function setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Clear auth token
 */
export function clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
}

/**
 * Get stored user data
 */
export function getStoredUser(): KaksosUser | null {
    const data = localStorage.getItem(USER_KEY);
    return data ? JSON.parse(data) : null;
}

/**
 * Store user data
 */
export function setStoredUser(user: KaksosUser): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Handle token expiry - clear storage and redirect to login
 */
function handleTokenExpired(): void {
    clearToken();
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
}

// ============================================
// MEMBER TOKEN STORE (Loop 1 cut 2, MEMBER-TOKEN-PORTAL-CARRY)
// ============================================
// One record per Kaksos id, keyed boby_member_token_<kaksosId>. Separate from the
// owner's boby_kaksos_* keys by design (Ruling A: the Kaksos id stored is the one the
// claim response returned, never derived from a person id). The token is a credential
// (WISDOM 19/22): never logged, never echoed, read only at send time.

const MEMBER_TOKEN_KEY_PREFIX = 'boby_member_token_';

export interface MemberTokenRecord {
    memberToken: string;
    kaksosId: string;
    circleLevel: string;
    expiresAt: string;
    memberName?: string;
}

/**
 * Return the stored member token for a Kaksos id when present and unexpired;
 * otherwise remove the key and return null.
 */
export function getMemberToken(kaksosId: string): string | null {
    const key = MEMBER_TOKEN_KEY_PREFIX + kaksosId;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
        const record = JSON.parse(raw) as MemberTokenRecord;
        const expiresAt = new Date(record.expiresAt).getTime();
        if (!record.memberToken || Number.isNaN(expiresAt) || expiresAt <= Date.now()) {
            localStorage.removeItem(key);
            return null;
        }
        return record.memberToken;
    } catch {
        localStorage.removeItem(key);
        return null;
    }
}

/**
 * Store a member token record under its Kaksos id.
 */
export function setMemberToken(kaksosId: string, record: MemberTokenRecord): void {
    localStorage.setItem(MEMBER_TOKEN_KEY_PREFIX + kaksosId, JSON.stringify(record));
}

/**
 * Return the stored member token record for a Kaksos id when present and unexpired
 * (Loop 1 cut 4: the claimed member's name for the chat greeting); otherwise remove
 * the key and return null. The same validity rule as getMemberToken.
 */
export function getMemberTokenRecord(kaksosId: string): MemberTokenRecord | null {
    const key = MEMBER_TOKEN_KEY_PREFIX + kaksosId;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
        const record = JSON.parse(raw) as MemberTokenRecord;
        const expiresAt = new Date(record.expiresAt).getTime();
        if (!record.memberToken || Number.isNaN(expiresAt) || expiresAt <= Date.now()) {
            localStorage.removeItem(key);
            return null;
        }
        return record;
    } catch {
        localStorage.removeItem(key);
        return null;
    }
}

/**
 * Remove the stored member token record for a Kaksos id (THE KEY CUT, Ruling 9 of 7 Oct 2026:
 * the API has ended the key; the page drops it and tells the member in plain words).
 */
export function removeMemberToken(kaksosId: string): void {
    try {
        localStorage.removeItem(MEMBER_TOKEN_KEY_PREFIX + kaksosId);
    } catch {
        // nothing to drop, or storage unavailable; the page proceeds as a visitor either way
    }
}

// ============================================
// TYPES
// ============================================

export interface KaksosUser {
    id: string;
    homeBobyPlaceId: string;
    email: string;
    firstName: string;
    lastName: string;
    roles: string[];
    sponsorCode?: string;
    bond_status?: string;
    verification_deadline?: string | null;
}

// ============================================
// AUTH API
// ============================================

export const authApi = {
    /**
     * Login with email and password
     */
    async login(email: string, password: string): Promise<{
        token: string;
        user: KaksosUser;
    }> {
        const response = await fetch(`${AUTH_API_URL}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        });

        const data = await response.json();

        if (!data.success) {
            throw new Error(data.error || 'Login failed');
        }

        const user = data.user;

        // Store token and user
        if (data.token) {
            setToken(data.token);
            setStoredUser({
                id: user.id,
                homeBobyPlaceId: user.homeBobyPlaceId || user.home_boby_place_id || user.id,
                email: user.email,
                firstName: user.first_name || user.firstName || 'User',
                lastName: user.last_name || user.lastName || '',
                roles: user.roles || [],
                sponsorCode: user.sponsor_code || user.sponsorCode,
                bond_status: user.bond_status,
                verification_deadline: user.verification_deadline,
            });
        }

        return {
            token: data.token,
            user: {
                id: user.id,
                homeBobyPlaceId: user.homeBobyPlaceId || user.home_boby_place_id || user.id,
                email: user.email,
                firstName: user.first_name || user.firstName || 'User',
                lastName: user.last_name || user.lastName || '',
                roles: user.roles || [],
                sponsorCode: user.sponsor_code || user.sponsorCode,
                bond_status: user.bond_status,
                verification_deadline: user.verification_deadline,
            },
        };
    },

    /**
     * Verify current token
     */
    async verify(): Promise<{
        valid: boolean;
        user: KaksosUser;
    }> {
        const token = getToken();
        if (!token) {
            throw new Error('No token');
        }

        const response = await fetch(`${AUTH_API_URL}/api/auth/me`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
        });

        const data = await response.json();

        if (!data.success) {
            handleTokenExpired();
            throw new Error(data.error || 'Token invalid');
        }

        const user = data.user;

        const kaksosUser: KaksosUser = {
            id: user.id,
            homeBobyPlaceId: user.homeBobyPlaceId || user.home_boby_place_id || user.id,
            email: user.email,
            firstName: user.first_name || user.firstName || 'User',
            lastName: user.last_name || user.lastName || '',
            roles: user.roles || [],
            sponsorCode: user.sponsor_code || user.sponsorCode,
            bond_status: user.bond_status,
            verification_deadline: user.verification_deadline,
        };

        setStoredUser(kaksosUser);

        return { valid: true, user: kaksosUser };
    },

    /**
     * Logout - clear tokens
     */
    logout(): void {
        clearToken();
    },
};

// ============================================
// CONVERSATION TYPES
// ============================================

export interface Conversation {
    id: number;
    conversation_id: string;
    peeler_id: string | null;
    boby_place_id: string;
    user_message: string;
    kaksos_response: string;
    circle: 'center' | 'inner' | 'mid' | 'outer' | 'public';
    tokens_input: number;
    tokens_output: number;
    cost: number;
    duration_ms: number;
    created_at: string;
    user_name?: string;
    user_email?: string;
    place_name?: string;
    messages?: string | null;
    title?: string | null;
    visitor_name?: string | null;
    visitor_email?: string | null;
    source_type?: string | null;
}

export interface ConversationsResponse {
    success: boolean;
    data: Conversation[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        offset: number;
        totalPages: number;
        hasMore: boolean;
    };
}

export interface ConversationResponse {
    success: boolean;
    conversation: Conversation;
}

// ============================================
// SETTINGS TYPES
// ============================================

export interface KaksosSettings {
    boby_place_id: string;
    kaksos_name: string;
    communication_style: string;
    response_length: string;
    proactiveness: string;
    custom_instructions: string;
    value_framework: string | null;
    preferred_model: string;
    // Chat limits
    public_chat_enabled: boolean;
    public_chat_daily_messages: number;
    public_chat_daily_tokens: number;
    public_chat_limit_action: 'polite_decline' | 'redirect_to_owner';
    member_chat_enabled: boolean;
    member_chat_daily_messages: number;
    member_chat_daily_tokens: number;
}

export interface SettingsResponse {
    success: boolean;
    has_settings: boolean;
    has_api_key: boolean;
    api_key_preview: string | null;
    settings: KaksosSettings;
}

export interface ApiKeyResponse {
    success: boolean;
    message?: string;
    has_api_key: boolean;
    api_key_preview: string | null;
}

export interface ApiKeyTestResponse {
    success: boolean;
    valid: boolean;
    message: string;
}

// ============================================
// SETTINGS API
// ============================================

// Settings API uses kaksos.getboby.ai (routes to kaksos-api via Cloudflare Worker)
export const KAKSOS_API_URL = (import.meta.env?.VITE_KAKSOS_API_URL as string) || 'https://kaksos.getboby.ai';

export const settingsApi = {
    /**
     * Fetch settings for a Boby Place
     */
    async getSettings(bobyPlaceId: string): Promise<SettingsResponse> {
        const token = getToken();

        const response = await fetch(`${KAKSOS_API_URL}/api/settings/${bobyPlaceId}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch settings: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Save settings for a Boby Place
     */
    async saveSettings(bobyPlaceId: string, settings: Partial<KaksosSettings>): Promise<{ success: boolean }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/settings/${bobyPlaceId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(settings),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to save settings: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Test settings with a preview message
     */
    async testPreview(params: {
        boby_place_id?: string;
        kaksos_name?: string;
        communication_style?: string;
        response_length?: string;
        proactiveness?: string;
        custom_instructions?: string;
        test_question: string;
    }): Promise<{
        success: boolean;
        response: string;
        settings_used: {
            kaksos_name: string;
            communication_style: string;
            response_length: string;
            proactiveness: string;
        };
    }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/settings/test-preview`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(params),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Test failed: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Save API key (encrypted on server)
     */
    async saveApiKey(bobyPlaceId: string, apiKey: string): Promise<ApiKeyResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/settings/${bobyPlaceId}/api-key`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ api_key: apiKey }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to save API key: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Remove API key
     */
    async removeApiKey(bobyPlaceId: string): Promise<ApiKeyResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/settings/${bobyPlaceId}/api-key`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to remove API key: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Test API key validity
     */
    async testApiKey(bobyPlaceId: string): Promise<ApiKeyTestResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/settings/${bobyPlaceId}/api-key/test`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to test API key: ${response.status}`);
        }

        return response.json();
    },
};

// ============================================
// CONVERSATIONS API
// ============================================

export const conversationsApi = {
    /**
     * List conversations for a Boby Place
     */
    async list(bobyPlaceId: string, options?: {
        limit?: number;
        offset?: number;
        circle?: string;
        search?: string;
    }): Promise<ConversationsResponse> {
        const token = getToken();

        const params = new URLSearchParams({
            bobyPlaceId,
            limit: String(options?.limit || 20),
            offset: String(options?.offset || 0),
        });

        if (options?.circle) {
            params.append('circle', options.circle);
        }
        if (options?.search) {
            params.append('search', options.search);
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/conversations?${params}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch conversations: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Get a single conversation by ID
     */
    async get(id: number, bobyPlaceId: string): Promise<ConversationResponse> {
        const token = getToken();

        const response = await fetch(`${KAKSOS_API_URL}/api/conversations/${id}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch conversation: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Delete a conversation
     */
    async delete(id: number, bobyPlaceId: string): Promise<{ success: boolean; message: string }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/conversations/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to delete conversation: ${response.status}`);
        }

        return response.json();
    },
};

// ============================================
// KMKY TYPES (Know Me Know You)
// ============================================

export interface KmkyMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    created_at: string;
}

export interface KmkyConversation {
    id: string;
    title: string;
    status: 'active' | 'archived' | 'planted';
    message_count: number;
    created_at: string;
    updated_at: string;
    messages?: KmkyMessage[];
}

export interface KmkyConversationsResponse {
    success: boolean;
    conversations: KmkyConversation[];
    pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
    };
}

export interface KmkyConversationResponse {
    success: boolean;
    conversation: KmkyConversation;
}

export interface KmkyMessageResponse {
    success: boolean;
    userMessage: KmkyMessage;
    assistantMessage: KmkyMessage;
    messageCount: number;
    newTitle?: string;
}

export interface KmkyStatistics {
    conversations: {
        total: number;
        active: number;
        planted: number;
    };
    qaPairs: {
        total: number;
        pending: number;
        trained: number;
        rejected: number;
    };
}

// ============================================
// KMKY API
// ============================================

export const kmkyApi = {
    /**
     * Create a new conversation
     */
    async createConversation(bobyPlaceId: string, title?: string): Promise<{ success: boolean; conversationId: string }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/kmky/conversations`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ bobyPlaceId, title }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to create conversation: ${response.status}`);
        }

        return response.json();
    },

    /**
     * List conversations
     */
    async listConversations(bobyPlaceId: string, options?: {
        status?: 'active' | 'archived' | 'planted';
        limit?: number;
        offset?: number;
    }): Promise<KmkyConversationsResponse> {
        const token = getToken();

        const params = new URLSearchParams({
            bobyPlaceId,
            limit: String(options?.limit || 50),
            offset: String(options?.offset || 0),
        });

        if (options?.status) {
            params.append('status', options.status);
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/kmky/conversations?${params}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch conversations: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Get a conversation with messages
     */
    async getConversation(conversationId: string, bobyPlaceId: string): Promise<KmkyConversationResponse> {
        const token = getToken();

        const response = await fetch(`${KAKSOS_API_URL}/api/kmky/conversations/${conversationId}?bobyPlaceId=${bobyPlaceId}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch conversation: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Send a message in a conversation
     */
    async sendMessage(conversationId: string, content: string, bobyPlaceId: string): Promise<KmkyMessageResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/kmky/conversations/${conversationId}/messages`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ content, bobyPlaceId }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to send message: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Archive (plant) a conversation - extracts QA pairs
     */
    async archiveConversation(conversationId: string, bobyPlaceId: string): Promise<{
        success: boolean;
        qaCount: number;
        qaPairs: Array<{ question: string; answer: string }>;
        conversationDeleted: boolean;
    }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/kmky/conversations/${conversationId}/archive`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ bobyPlaceId }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to archive conversation: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Delete a conversation
     */
    async deleteConversation(conversationId: string, bobyPlaceId: string): Promise<{ success: boolean }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/kmky/conversations/${conversationId}?bobyPlaceId=${bobyPlaceId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to delete conversation: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Get KMKY statistics
     */
    async getStatistics(bobyPlaceId: string): Promise<{ success: boolean; statistics: KmkyStatistics }> {
        const token = getToken();

        const response = await fetch(`${KAKSOS_API_URL}/api/kmky/statistics?bobyPlaceId=${bobyPlaceId}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch statistics: ${response.status}`);
        }

        return response.json();
    },
};

// ============================================
// QA ARCHIVE TYPES (Nurture)
// ============================================

export interface QAPair {
    id: string;
    conversation_id: string;
    question_number: number;
    question: string;
    answer: string;
    circle_assignment: 'center' | 'inner' | 'mid' | 'outer' | 'public';
    training_status: 'pending' | 'trained' | 'rejected' | 'tested';
    trained_as_type?: string;
    training_example_id?: number;
    seed_id?: string;
    created_at: string;
    trained_at?: string;
    importance: number;
    source_peeler_id?: string;
    source_peeler_name?: string;
    source_type: 'kmky' | 'public';
    personal_only: boolean;
    wrong_answer?: string;
    training_feedback?: string;
}

export interface QAArchiveResponse {
    success: boolean;
    count: number;
    qaPairs: QAPair[];
}

export interface QATrainResponse {
    success: boolean;
    message: string;
    trainingExampleId?: number;
}

// ============================================
// QA ARCHIVE API (Nurture)
// ============================================

export const qaArchiveApi = {
    /**
     * List QA pairs from archive
     */
    async list(bobyPlaceId: string, options?: {
        trainingStatus?: 'pending' | 'trained' | 'rejected' | 'tested';
        circleLevel?: string;
        limit?: number;
        offset?: number;
    }): Promise<QAArchiveResponse> {
        const token = getToken();

        const params = new URLSearchParams({
            bobyPlaceId,
            limit: String(options?.limit || 100),
            offset: String(options?.offset || 0),
        });

        if (options?.trainingStatus) {
            params.append('trainingStatus', options.trainingStatus);
        }
        if (options?.circleLevel) {
            params.append('circleLevel', options.circleLevel);
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/kmky/qa-archive?${params}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch QA archive: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Train a QA pair (approve, correct, reject)
     */
    async train(qaId: string, params: {
        action: 'approve' | 'correct' | 'reject' | 'tested';
        bobyPlaceId: string;
        correctedAnswer?: string;
        circleLevel?: string;
    }): Promise<QATrainResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/kmky/qa-archive/${qaId}/train`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(params),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to train QA pair: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Update circle assignment for a QA pair
     */
    async updateCircle(qaId: string, circleLevel: string, bobyPlaceId: string): Promise<{ success: boolean }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/kmky/qa-archive/${qaId}/circle?bobyPlaceId=${bobyPlaceId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ circleLevel }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to update circle: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Delete a QA pair
     */
    async delete(qaId: string, bobyPlaceId: string): Promise<{ success: boolean }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/kmky/qa-archive/${qaId}?bobyPlaceId=${bobyPlaceId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to delete QA pair: ${response.status}`);
        }

        return response.json();
    },
};

// ============================================
// SOWING TYPES
// ============================================

export interface SowingAnswer {
    question_id: number;
    answer_text: string;
    circle_level: 'center' | 'inner' | 'mid' | 'outer';
    is_living_memory?: boolean;
    answered_at?: string;
}

export interface SowingAnswersResponse {
    success: boolean;
    answers: SowingAnswer[];
}

export interface SowingCompleteResponse {
    success: boolean;
    message: string;
    instructions?: string;
    instructionPreview?: string;
    seedCount?: number;
    seedsStored?: number;
    metadata?: {
        generator: string;
        ownerName: string;
        categoryCounts?: Record<string, number>;
    };
}

// ============================================
// SOWING API
// ============================================

export const sowingApi = {
    /**
     * Get existing answers for the current user
     */
    async getAnswers(): Promise<SowingAnswersResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/sowing/answers`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch answers: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Save or update an answer (upsert)
     */
    async saveAnswer(params: {
        question_id: number;
        answer_text: string;
        circle_level?: 'center' | 'inner' | 'mid' | 'outer';
        is_living_memory?: boolean;
    }): Promise<{ success: boolean; message: string; verified?: boolean }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/sowing/answer`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(params),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to save answer: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Reset all answers for the current user
     */
    async resetAnswers(): Promise<{ success: boolean; message: string }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/sowing/reset`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to reset answers: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Complete the sowing session
     */
    async complete(params: {
        bobyPlaceId: string;
        answers?: Record<string, string>;
        tier?: 'starter' | 'deep' | 'complete';
        kaksosName?: string;
    }): Promise<SowingCompleteResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/sowing/complete`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(params),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to complete sowing: ${response.status}`);
        }

        return response.json();
    },
};

// ============================================
// CIRCLE MANAGEMENT TYPES
// ============================================

export type CircleLevel = 'center' | 'inner' | 'mid' | 'outer' | 'public';

export interface CircleMember {
    id: string;
    peeler_id: string;
    peeler_email: string | null;
    peeler_name: string | null;
    circle_level: CircleLevel;
    relationship_notes: string | null;
    invited_at: string;
    accepted_at: string | null;
    created_at: string;
    updated_at: string;
    switch_on: boolean;
}

export interface CircleStats {
    center: number;
    inner: number;
    mid: number;
    outer: number;
    public: number;
}

export interface CircleMembersResponse {
    success: boolean;
    members: CircleMember[];
    stats: CircleStats;
    total: number;
}

export interface CircleMemberResponse {
    success: boolean;
    message: string;
    member?: CircleMember;
}

export interface CircleLookupResponse {
    success: boolean;
    alreadyInCircle: boolean;
    found?: boolean;
    member?: CircleMember;
    peeler?: {
        id: string;
        telepathcode?: string;
        email: string;
        name: string | null;
        kaksos_name?: string | null;
        legal_name?: string | null;
    } | null;
    message?: string;
}

export interface IssueLinkResponse {
    request_id: string;
    status: string;
    circle_level: CircleLevel;
    claim_url: string;
    token_expires_at: string;
    revoked_prior: number;
}

export interface SwitchResponse {
    request_id?: string;
    status?: string;
    circle_level?: CircleLevel;
    token_expires_at?: string;
    revoked_prior: number;
}

// ============================================
// CIRCLE MANAGEMENT API
// ============================================

export const circlesApi = {
    /**
     * List all circle members
     */
    async listMembers(bobyPlaceId: string, circleLevel?: CircleLevel | 'all'): Promise<CircleMembersResponse> {
        const token = getToken();

        const params = new URLSearchParams({ bobyPlaceId });
        if (circleLevel && circleLevel !== 'all') {
            params.append('circleLevel', circleLevel);
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/circles/members?${params}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch members: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Add a new member to a circle
     */
    async addMember(params: {
        bobyPlaceId: string;
        peelerId: string;
        peelerEmail?: string;
        peelerName?: string;
        circleLevel: CircleLevel;
        relationshipNotes?: string;
    }): Promise<CircleMemberResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/circles/members`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': params.bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
            body: JSON.stringify(params),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to add member: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Seat the Keeper (GOAL_KeeperKey_v0_1 W-2 (a)): the Kaksos's own agent seat, never a peeler.
     * One per place; inner, mid or outer. The API mints the Keeper's id; no TelePathCode is sent.
     */
    async addKeeper(bobyPlaceId: string, name: string, circleLevel: CircleLevel): Promise<CircleMemberResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/circles/keeper`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
            body: JSON.stringify({ name, circleLevel }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.message || error.error || `Failed to add the Keeper: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Issue a claim link to an existing member (Loop 1 cut 4 (a); the cut 1 route).
     * The reply carries the claim_url once; the caller holds it in component state only.
     */
    async issueLink(bobyPlaceId: string, membershipId: string): Promise<IssueLinkResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/connections/members/${membershipId}/issue-link`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
            body: '{}',
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || error.message || `Failed to issue link: ${response.status}`);
        }

        return response.json();
    },

    /**
     * The owner's switch (Loop 1 step four, cut 6 (a)): Connected on or off for a member.
     * The reply never carries a link or a token; on mints one the member collects by sign-in.
     */
    async setSwitch(bobyPlaceId: string, membershipId: string, on: boolean): Promise<SwitchResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/connections/members/${membershipId}/switch`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
            body: JSON.stringify({ on }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || error.message || `Failed to change the connection: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Update a member's circle level or details
     */
    async updateMember(id: string, bobyPlaceId: string, updates: {
        circleLevel?: CircleLevel;
        peelerName?: string;
        peelerEmail?: string;
        relationshipNotes?: string;
    }): Promise<{ success: boolean; message: string }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/circles/members/${id}?bobyPlaceId=${bobyPlaceId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
            body: JSON.stringify(updates),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to update member: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Remove a member from circles
     */
    async removeMember(id: string, bobyPlaceId: string): Promise<{ success: boolean; message: string }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/circles/members/${id}?bobyPlaceId=${bobyPlaceId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to remove member: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Look up a member by TelePathCode
     */
    async lookupMember(peelerId: string, bobyPlaceId: string): Promise<CircleLookupResponse> {
        const token = getToken();

        const response = await fetch(`${KAKSOS_API_URL}/api/circles/members/${peelerId}/lookup?bobyPlaceId=${bobyPlaceId}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to lookup member: ${response.status}`);
        }

        return response.json();
    },
};

// ============================================
// NURTURE v2 TYPES
// ============================================

export interface NurtureChatResponse {
    responseId: string;
    message: string;
    circleLevel: string;
    memberContext: string;
    memoryContext: {
        totalMemories: number;
        byType: {
            seeds: number;
            personalSubsets: number;
            qaMemories: number;
        };
        byCircle: Record<string, number>;
    };
    sources: Array<{
        type: string;
        seedId?: string;
        subsetId?: string;
        peelerId?: string;
        peelerName?: string;
        circle?: string;
    }>;
    timestamp: string;
}

export interface NurtureFeedbackResponse {
    success: boolean;
    feedbackId: string;
    message: string;
}

export interface NurturePendingSeedsResponse {
    needsReview: Array<{
        id: string;
        question: string;
        answer: string;
        circle_level: string;
        source_type: string | null;
        category: string;
        review_reason: string;
        updated_at: string;
    }>;
    pendingCorrections: Array<{
        id: string;
        suggested_question: string | null;
        suggested_answer: string;
        suggested_circle: string | null;
        source_type: string | null;
        status: string;
        created_at: string;
        correction_text: string;
        response_id: string;
    }>;
    recentRevocations: Array<{
        id: string;
        fact_summary: string;
        peeler_name: string;
        revoked_reason: string;
        revoked_at: string;
    }>;
    summary: {
        totalNeedsAttention: number;
        reviewCount: number;
        correctionCount: number;
        revocationCount: number;
    };
}

export interface NurtureStatsResponse {
    seeds: {
        byCircle: Record<string, { total: number; active: number }>;
        total: number;
    };
    personalSubsets: {
        total: number;
        active: number;
        uniqueContributors: number;
    };
    activity: {
        seedsThisWeek: number;
        seedsThisMonth: number;
    };
    feedback: {
        total: number;
        good: number;
        corrections: number;
        never: number;
    };
    circles: Record<string, number>;
}

export interface NurtureCircleMembersResponse {
    members: Array<{
        id: string;
        member_user_id: string;
        member_name: string;
        member_email: string;
        circle_level: string;
        created_at: string;
    }>;
    total: number;
}

// ============================================
// NURTURE v2 API
// ============================================

// ============================================
// WATCH GROW TYPES (Long-Term Memory)
// ============================================

export interface WatchGrowSeed {
    id: string;
    question: string;
    answer: string;
    circle_level: CircleLevel;
    source_peeler_id: string | null;
    source_peeler_name: string | null;
    source_circle_level: string | null;
    category: string | null;
    created_at: string;
    trained_at: string | null;
    is_boundary: boolean;
    effective_buoyancy: string;
    raw_buoyancy: string;
    maturity_percent: number;
    can_uproot: boolean;
}

export interface WatchGrowSeedsResponse {
    success: boolean;
    seeds: WatchGrowSeed[];
    pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
    };
}

export interface WatchGrowStatsResponse {
    success: boolean;
    stats: {
        totalSeeds: number;
        boundarySeeds: number;
        byCircle: Record<string, { total: number; active: number }>;
        growth: {
            thisWeek: number;
            thisMonth: number;
        };
        timeline: Array<{
            weekStart: string;
            seedsAdded: number;
        }>;
        uniqueContributors: number;
        buoyancyDepth: {
            surface: number;
            midDepth: number;
            deep: number;
        };
    };
}

export interface WatchGrowSeedResponse {
    success: boolean;
    seed: WatchGrowSeed;
}

export interface WatchGrowBoundaryResponse {
    success: boolean;
    message: string;
    is_boundary: boolean;
}

// ============================================
// WATCH GROW API (Long-Term Memory)
// ============================================

// ============================================
// SEEDS LIBRARY TYPES
// ============================================

export interface SeedQuestion {
    id: number;
    questionNumber: number;
    questionText: string;
    helperText: string | null;
    category: 'essential' | 'deep_roots' | 'complete';
    subcategory: string | null;
    answer: string | null;
    source: 'settings' | 'library' | null;
    updatedAt: string | null;
}

export interface SeedsLibraryQuestionsResponse {
    success: boolean;
    questions: SeedQuestion[];
    stats: {
        total: number;
        answered: number;
        unanswered: number;
    };
}

export interface SeedsLibraryStatsResponse {
    total: number;
    answered: number;
    unanswered: number;
    byCategory: Record<string, { total: number; answered: number }>;
    bySource: Record<string, number>;
}

export interface SeedsLibraryAnswerResponse {
    success: boolean;
    questionId: number;
    answer: string;
    source: string;
    updatedAt: string;
}

// ============================================
// SEEDS LIBRARY API
// ============================================

export const seedsLibraryApi = {
    /**
     * Get all 99 questions with answers
     */
    async getQuestions(bobyPlaceId: string, options?: {
        category?: 'essential' | 'deep_roots' | 'complete' | 'all';
        answered?: boolean;
    }): Promise<SeedsLibraryQuestionsResponse> {
        const token = getToken();

        const params = new URLSearchParams();
        if (options?.category && options.category !== 'all') {
            params.append('category', options.category);
        }
        if (options?.answered !== undefined) {
            params.append('answered', String(options.answered));
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/seeds-library/questions?${params}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch questions: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Save or update an answer
     */
    async saveAnswer(bobyPlaceId: string, questionId: number, answer: string): Promise<SeedsLibraryAnswerResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/seeds-library/questions/${questionId}/answer`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
            body: JSON.stringify({ answer }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to save answer: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Get summary statistics
     */
    async getStats(bobyPlaceId: string): Promise<SeedsLibraryStatsResponse> {
        const token = getToken();

        const response = await fetch(`${KAKSOS_API_URL}/api/seeds-library/stats`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch stats: ${response.status}`);
        }

        return response.json();
    },
};

export const watchGrowApi = {
    /**
     * List permanent seeds (Long-Term Memory)
     */
    async getSeeds(bobyPlaceId: string, options?: {
        circle?: CircleLevel | 'all';
        search?: string;
        limit?: number;
        offset?: number;
        includeBoundary?: boolean;
        sort?: 'buoyancy' | 'newest' | 'oldest';
    }): Promise<WatchGrowSeedsResponse> {
        const token = getToken();

        const params = new URLSearchParams({
            limit: String(options?.limit || 50),
            offset: String(options?.offset || 0),
        });

        if (options?.circle && options.circle !== 'all') {
            params.append('circle', options.circle);
        }
        if (options?.search) {
            params.append('search', options.search);
        }
        if (options?.includeBoundary) {
            params.append('includeBoundary', 'true');
        }
        if (options?.sort) {
            params.append('sort', options.sort);
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/watch-grow/seeds?${params}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch seeds: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Get growth statistics
     */
    async getStats(bobyPlaceId: string): Promise<WatchGrowStatsResponse> {
        const token = getToken();

        const response = await fetch(`${KAKSOS_API_URL}/api/watch-grow/stats`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch stats: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Get a specific seed's full details
     */
    async getSeed(bobyPlaceId: string, seedId: string): Promise<WatchGrowSeedResponse> {
        const token = getToken();

        const response = await fetch(`${KAKSOS_API_URL}/api/watch-grow/seeds/${seedId}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch seed: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Mark a seed as boundary (suppressed) - per "No Unsee" principle
     */
    async setBoundary(bobyPlaceId: string, seedId: string, isBoundary: boolean, reason?: string): Promise<WatchGrowBoundaryResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/watch-grow/seeds/${seedId}/boundary`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
            body: JSON.stringify({ is_boundary: isBoundary, reason }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to update boundary: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Delete a seed (uproot) - only works for seeds not yet at 100%
     */
    async deleteSeed(bobyPlaceId: string, seedId: string): Promise<{ success: boolean; message: string }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/watch-grow/seeds/${seedId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Failed to uproot seed: ${response.status}`);
        }

        return response.json();
    },
};

export const nurtureApi = {
    /**
     * Test a message as a specific circle member
     */
    async chat(params: {
        bobyPlaceId: string;
        message: string;
        circleLevel: CircleLevel;
        memberId?: string;
        memberName?: string;
    }): Promise<NurtureChatResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/nurture/chat`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': params.bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
            body: JSON.stringify({
                message: params.message,
                circleLevel: params.circleLevel,
                memberId: params.memberId,
                memberName: params.memberName,
            }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Chat failed: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Record feedback on a response with multi-circle support
     */
    async feedback(params: {
        bobyPlaceId: string;
        responseId: string;
        feedbackType: 'good' | 'correct' | 'never';
        correction?: string;
        seedId?: string;
        reason?: string;
        // Fields for creating trained seeds
        question?: string;
        answer?: string;
        // Multi-circle support - array of circles to save to
        circleLevels?: CircleLevel[];
        testerName?: string;
    }): Promise<NurtureFeedbackResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/nurture/feedback`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': params.bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
            body: JSON.stringify({
                responseId: params.responseId,
                feedbackType: params.feedbackType,
                correction: params.correction,
                seedId: params.seedId,
                reason: params.reason,
                question: params.question,
                answer: params.answer,
                // Send array of circles for multi-circle approval
                circleLevels: params.circleLevels,
                testerName: params.testerName,
            }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Unknown error' }));
            throw new Error(error.message || `Feedback failed: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Get seeds needing attention
     */
    async getPendingSeeds(bobyPlaceId: string): Promise<NurturePendingSeedsResponse> {
        const token = getToken();

        const response = await fetch(`${KAKSOS_API_URL}/api/nurture/pending-seeds`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch pending seeds: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Get nurture statistics
     */
    async getStats(bobyPlaceId: string): Promise<NurtureStatsResponse> {
        const token = getToken();

        const response = await fetch(`${KAKSOS_API_URL}/api/nurture/stats`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch stats: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Get circle members for perspective selector
     */
    async getCircleMembers(bobyPlaceId: string, circle?: CircleLevel): Promise<NurtureCircleMembersResponse> {
        const token = getToken();

        const params = new URLSearchParams();
        if (circle) {
            params.append('circle', circle);
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/nurture/circle-members?${params}`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': getStoredUser()?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch circle members: ${response.status}`);
        }

        return response.json();
    },
};

// ============================================
// PUBLIC CHAT TYPES (No Auth Required)
// ============================================

export interface PublicKaksosInfo {
    peelerId: string;
    bobyPlaceId: string;
    kaksosName: string;
    ownerName: string;
}

export interface PublicLookupResponse {
    success: boolean;
    error?: string;
    kaksos?: PublicKaksosInfo;
}

export interface PublicChatMessage {
    sender: 'user' | 'kaksos';
    text: string;
    timestamp: Date;
}

export interface PublicChatResponse {
    success: boolean;
    response?: string;
    error?: string;
    memberKeyEnded?: boolean;
}

// ============================================
// MEMBER CHAT TYPES (JWT Auth Required)
// ============================================

export interface MemberChatMessage {
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
}

export interface MemberChatConversation {
    id: string;
    memberName: string;
    memberCircle: CircleLevel;
    messages: MemberChatMessage[];
    createdAt: string;
    updatedAt: string;
}

export interface MemberChatHistoryResponse {
    success: boolean;
    conversations: MemberChatConversation[];
}

export interface MemberChatSendResponse {
    success: boolean;
    response?: string;
    conversationId?: string;
    memberName?: string;
    memberCircle?: string;
    sourceType?: string;
    limitReached?: boolean;
    error?: string;
}

// ============================================
// PUBLIC CHAT API (No Auth Required)
// ============================================

export const publicChatApi = {
    /**
     * Look up a Kaksos by TelePathCode
     */
    async lookup(code: string): Promise<PublicLookupResponse> {
        const response = await fetch(`${KAKSOS_API_URL}/api/public/kaksos-lookup?code=${encodeURIComponent(code)}`, {
            headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
            throw new Error(`Lookup failed: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Send a message to a Kaksos (public chat)
     */
    async sendMessage(params: {
        bobyPlaceId: string;
        sessionId: string;
        visitorName?: string;
        visitorEmail?: string;
        message: string;
        conversationHistory?: PublicChatMessage[];
    }): Promise<PublicChatResponse> {
        const memberToken = getMemberToken(params.bobyPlaceId);
        const response = await fetch(`${KAKSOS_API_URL}/api/public/kaksos-chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                bobyPlaceId: params.bobyPlaceId,
                sessionId: params.sessionId,
                visitorName: params.visitorName,
                visitorEmail: params.visitorEmail,
                visitorCircle: 'public',
                ...(memberToken ? { memberToken } : {}),
                message: params.message,
                conversationHistory: params.conversationHistory?.map(m => ({
                    sender: m.sender,
                    text: m.text,
                })),
            }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            // THE KEY CUT (Ruling 9 of 7 Oct 2026): the API has ended the presented key; a distinct result the page can test
            if (response.status === 403 && error.error === 'member_key_ended') {
                return { success: false, error: 'member_key_ended', memberKeyEnded: true };
            }
            return { success: false, error: error.error || `Chat failed: ${response.status}` };
        }

        return response.json();
    },

    /**
     * Send conversation to the Kaksos owner for review
     */
    async sendConversation(params: {
        bobyPlaceId: string;
        sessionId: string;
        visitorName: string;
        visitorEmail?: string;
        conversationHistory: PublicChatMessage[];
    }): Promise<{ success: boolean; message?: string; conversationId?: string; error?: string; memberKeyEnded?: boolean; memberCopy?: { kaksosName: string } | null }> {
        const memberToken = getMemberToken(params.bobyPlaceId);
        const response = await fetch(`${KAKSOS_API_URL}/api/public/send-conversation`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                bobyPlaceId: params.bobyPlaceId,
                sessionId: params.sessionId,
                visitorName: params.visitorName,
                visitorEmail: params.visitorEmail,
                ...(memberToken ? { memberToken } : {}),
                conversationHistory: params.conversationHistory.map(m => ({
                    sender: m.sender,
                    text: m.text,
                })),
            }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            // THE KEY CUT (Ruling 9 of 7 Oct 2026): the API has ended the presented key; a distinct result the page can test
            if (response.status === 403 && error.error === 'member_key_ended') {
                return { success: false, error: 'member_key_ended', memberKeyEnded: true };
            }
            return { success: false, error: error.error || `Send failed: ${response.status}` };
        }

        return response.json();
    },
};

// ============================================
// MEMBER API (JWT Auth Required) - Loop 1 step four, cut 6 (b)
// ============================================
// The signed-in person as a member of other people's Kaksos. Both calls send the
// portal JWT; the API reads the peeler id from its signature (never a header). The
// session-claim reply carries the member token once; the caller stores it and drops it.
// Neither function logs.

export interface MemberKaksosRow {
    boby_place_id: string;
    circle_level: string;
    telepathcode: string | null;
    kaksos_name: string | null;
    owner_first_name: string | null;
    switch_on: boolean;
}

export interface SessionClaimResponse {
    member_token: string;
    boby_place_id: string;
    circle_level: string;
    token_expires_at: string;
    telepathcode: string | null;
    kaksos_name: string | null;
    member_name: string | null;
}

export const memberApi = {
    /**
     * List the Kaksos the signed-in person is a member of
     */
    async listKaksos(): Promise<{ success: boolean; kaksos: MemberKaksosRow[] }> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/member/kaksos`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.error || error.message || `Failed to load your Kaksos: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Collect a member token for one Kaksos, proven by the sign-in (the owner must have switched it on)
     */
    async sessionClaim(kaksosId: string): Promise<SessionClaimResponse> {
        const token = getToken();

        if (!token) {
            throw new Error('Authentication required');
        }

        const response = await fetch(`${KAKSOS_API_URL}/api/member/kaksos/${kaksosId}/session-claim`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: '{}',
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            // The status leads the message so the page can tell a 403 (switched off) from the rest
            throw new Error(`${response.status}: ${error.error || error.message || 'Could not open the chat'}`);
        }

        return response.json();
    },
};

// ============================================
// MEMBER CHAT API (JWT Auth Required)
// ============================================

export const memberChatApi = {
    /**
     * Send a message as a circle member to a Kaksos
     */
    async send(params: {
        bobyPlaceId: string;
        memberPeelerId: string;
        message: string;
        conversationId?: string;
    }): Promise<MemberChatSendResponse> {
        const token = getToken();
        if (!token) throw new Error('Authentication required');

        const user = getStoredUser();
        const response = await fetch(`${KAKSOS_API_URL}/api/member-chat/send`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': params.bobyPlaceId,
                'X-User-Id': user?.id || '',
            },
            body: JSON.stringify({
                memberPeelerId: params.memberPeelerId,
                message: params.message,
                conversationId: params.conversationId,
            }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            return { success: false, error: error.error || `Send failed: ${response.status}` };
        }

        return response.json();
    },

    /**
     * Load conversation history for a member
     */
    async history(bobyPlaceId: string, memberPeelerId: string): Promise<MemberChatHistoryResponse> {
        const token = getToken();
        if (!token) throw new Error('Authentication required');

        const user = getStoredUser();
        const params = new URLSearchParams({ memberPeelerId });

        const response = await fetch(`${KAKSOS_API_URL}/api/member-chat/history?${params}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': bobyPlaceId,
                'X-User-Id': user?.id || '',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch history: ${response.status}`);
        }

        return response.json();
    },

    /**
     * Archive a training simulation conversation — extract Q&A seeds on modal close
     */
    async archive(params: { bobyPlaceId: string; conversationId: string }): Promise<{ success: boolean; seedCount?: number }> {
        const token = getToken();
        if (!token) throw new Error('Authentication required');

        const user = getStoredUser();
        const response = await fetch(`${KAKSOS_API_URL}/api/member-chat/archive`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'X-Boby-Place-Id': params.bobyPlaceId,
                'X-User-Id': user?.id || '',
            },
            body: JSON.stringify({ conversationId: params.conversationId }),
        });

        return response.json();
    },
};
